import React from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  Chip,
  Button,
  Grid
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import FavoriteIcon from '@mui/icons-material/Favorite';
import Rating from '@mui/material/Rating';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';

/**
 * ItemDetailDrawer - Side panel showing full item details
 * 
 * Displays comprehensive information about a collection item:
 * - Cover art
 * - Title, platform, year
 * - Condition, edition, priority
 * - Notes
 * - Edit and delete actions
 */
const ItemDetailDrawer = ({
  open,
  onClose,
  item,
  onEdit,
  onDelete,
  onToggleWishlist,
  onToggleFavourite,
  onRate,
  collectionType = 'games'
}) => {
  if (!item) return null;

  const getImageUrl = () => {
    // Prefer processed image (background removed) for consoles/lego/music
    const processed = item.processedImageUrl || '';
    if (processed) return processed;

    const imageUrl = item.imageUrl || item.coverUrl || '';
    if (!imageUrl) return '';

    // Request full-size cover for detail view
    if (imageUrl.includes('images.igdb.com')) {
      return imageUrl.replace('/t_cover_small/', '/t_cover_big/');
    }
    return imageUrl;
  };

  const imageUrl = getImageUrl();

  // Format fields based on collection type
  const getFields = () => {
    const fields = [];

    // Common fields
    if (item.platform) {
      fields.push({ label: 'Platform', value: item.platform });
    }
    if (item.year || item.releaseDate) {
      fields.push({ label: 'Year', value: item.year || item.releaseDate?.substring(0, 4) });
    }
    if (item.condition) {
      fields.push({ label: 'Condition', value: item.condition });
    }
    if (item.edition) {
      fields.push({ label: 'Edition', value: item.edition });
    }
    if (item.rating) {
      fields.push({ label: 'Rating', value: item.rating, type: 'rating' });
    }
    if (item.priority) {
      fields.push({ label: 'Priority', value: item.priority });
    }

    // Game-specific
    if (collectionType === 'games') {
      if (item.played !== undefined) {
        fields.push({ label: 'Played', value: item.played ? 'Yes' : 'No' });
      }
      if (item.completed !== undefined) {
        fields.push({ label: 'Completed', value: item.completed ? 'Yes' : 'No' });
      }
    }

    // Movie-specific
    if (collectionType === 'movies' || collectionType === 'movie-wishlist') {
      if (item.format) {
        fields.push({ label: 'Format', value: item.format });
      }
      if (item.watched !== undefined) {
        fields.push({ label: 'Watched', value: item.watched ? 'Yes' : 'No' });
      }
      if (item.director) {
        fields.push({ label: 'Director', value: item.director });
      }
      if (item.genre) {
        fields.push({ label: 'Genre', value: item.genre });
      }
      if (item.runtime) {
        fields.push({ label: 'Runtime', value: item.runtime });
      }
    }

    // Music-specific
    if (collectionType === 'music' || collectionType === 'music-wishlist') {
      if (item.artist) {
        fields.push({ label: 'Artist', value: item.artist });
      }
      if (item.format) {
        fields.push({ label: 'Format', value: item.format });
      }
      if (item.genre) {
        fields.push({ label: 'Genre', value: item.genre });
      }
    }

    // LEGO-specific
    if (collectionType === 'lego') {
      if (item.setNumber) {
        fields.push({ label: 'Set Number', value: item.setNumber });
      }
      if (item.pieces) {
        fields.push({ label: 'Pieces', value: item.pieces });
      }
      if (item.theme) {
        fields.push({ label: 'Theme', value: item.theme });
      }
    }

    // Board Game-specific
    if (collectionType === 'board-games' || collectionType === 'board-game-wishlist') {
      if (item.minPlayers || item.maxPlayers) {
        const players = item.minPlayers === item.maxPlayers 
          ? `${item.minPlayers}` 
          : `${item.minPlayers || '?'}-${item.maxPlayers || '?'}`;
        fields.push({ label: 'Players', value: players });
      }
      if (item.playingTime) {
        fields.push({ label: 'Playing Time', value: `${item.playingTime} min` });
      }
      if (item.designers) {
        fields.push({ label: 'Designer(s)', value: item.designers });
      }
      if (item.publisher) {
        fields.push({ label: 'Publisher', value: item.publisher });
      }
      if (item.category) {
        fields.push({ label: 'Category', value: item.category });
      }
      if (item.played !== undefined) {
        fields.push({ label: 'Played', value: item.played ? 'Yes' : 'No' });
      }
    }

    return fields;
  };

  const fields = getFields();

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 400, md: 480 },
          padding: 0
        }
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: 2,
            borderBottom: 1,
            borderColor: 'divider'
          }}
        >
          <Typography variant="h6">Item Details</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflowY: 'auto', padding: 3 }}>
          {/* Cover image */}
          {imageUrl && (
            <Box
              sx={{
                width: '100%',
                marginBottom: 3,
                display: 'flex',
                justifyContent: 'center'
              }}
            >
              <Box
                component="img"
                src={imageUrl}
                alt={item.name || item.title}
                sx={{
                  maxWidth: '100%',
                  maxHeight: '400px',
                  objectFit: 'contain',
                  borderRadius: 1,
                  boxShadow: 3
                }}
              />
            </Box>
          )}

          {/* Title + Favourite toggle */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
            <Typography variant="h5" gutterBottom fontWeight={600} sx={{ flex: 1 }}>
              {item.name || item.title || 'Unknown'}
            </Typography>
            {onToggleFavourite && (
              <IconButton
                size="small"
                onClick={() => onToggleFavourite(item)}
                sx={{ mt: 0.5, flexShrink: 0 }}
              >
                {(item.favorite || item.favourite || item.isFavorite) ? (
                  <FavoriteIcon sx={{ color: '#ff4081' }} />
                ) : (
                  <FavoriteBorderIcon />
                )}
              </IconButton>
            )}
          </Box>

          {/* Wishlist indicator */}
          {item.inWishlist && (
            <Chip
              icon={<FavoriteIcon />}
              label="In Wishlist"
              color="error"
              size="small"
              sx={{ marginBottom: 2 }}
            />
          )}

          <Divider sx={{ marginY: 2 }} />

          {/* Inline rating */}
          {onRate && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                Your Rating
              </Typography>
              <Rating
                value={item.rating || 0}
                onChange={(_, newValue) => onRate(item, newValue)}
                size="large"
              />
            </Box>
          )}

          {/* Fields grid */}
          <Grid container spacing={2}>
            {fields.filter(f => f.type !== 'rating').map((field, index) => (
              <Grid item xs={6} key={index}>
                <Typography variant="caption" color="text.secondary" display="block">
                  {field.label}
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {field.value}
                </Typography>
              </Grid>
            ))}
          </Grid>

          {/* Notes */}
          {item.notes && (
            <>
              <Divider sx={{ marginY: 2 }} />
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                Notes
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {item.notes}
              </Typography>
            </>
          )}

          {/* Purchase info */}
          {(item.purchasePrice || item.purchaseDate) && (
            <>
              <Divider sx={{ marginY: 2 }} />
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                Purchase Information
              </Typography>
              {item.purchasePrice && (
                <Typography variant="body2">
                  Price: £{item.purchasePrice}
                </Typography>
              )}
              {item.purchaseDate && (
                <Typography variant="body2">
                  Date: {new Date(item.purchaseDate).toLocaleDateString()}
                </Typography>
              )}
            </>
          )}

          {/* Metadata */}
          <Divider sx={{ marginY: 2 }} />
          <Typography variant="caption" color="text.secondary" display="block">
            Added: {new Date(item.createdAt || Date.now()).toLocaleDateString()}
          </Typography>
          {item.updatedAt && item.updatedAt !== item.createdAt && (
            <Typography variant="caption" color="text.secondary" display="block">
              Updated: {new Date(item.updatedAt).toLocaleDateString()}
            </Typography>
          )}
        </Box>

        {/* Actions footer */}
        <Box
          sx={{
            padding: 2,
            borderTop: 1,
            borderColor: 'divider',
            display: 'flex',
            gap: 1,
            flexWrap: 'wrap'
          }}
        >
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => {
              if (onEdit) onEdit(item);
              onClose();
            }}
            fullWidth
          >
            Edit
          </Button>
          
          {onToggleWishlist && (
            <Button
              variant="outlined"
              startIcon={item.inWishlist ? <FavoriteIcon /> : <FavoriteBorderIcon />}
              onClick={() => onToggleWishlist(item)}
              fullWidth
            >
              {item.inWishlist ? 'Remove from Wishlist' : 'Add to Wishlist'}
            </Button>
          )}
          
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => {
              if (onDelete) onDelete(item);
              onClose();
            }}
            fullWidth
          >
            Delete
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default ItemDetailDrawer;
